import { useState, useEffect } from "react";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent } from "@shared/components/ui/card";
import { SelectCity } from "@features/settings/citys/SelectCity";
import { SelectUser } from "@features/users/SelectUser";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { endOfDay, startOfDay } from "date-fns";
import { X } from "lucide-react";

/**
 * Base Filter Component
 * A reusable filter component that can be configured for different entities (packages, pickups, etc.)
 * 
 * @param {Object} props
 * @param {Function} props.onFilter - Callback function when filters change
 * @param {Object} props.currentFilters - Current filter values
 * @param {Array} props.statusOptions - Array of status options for tabs
 * @param {Object} props.config - Configuration object for customizing the component
 * @param {boolean} props.config.showPhoneFilter - Show phone number filter
 * @param {boolean} props.config.showCityFilter - Show city filter  
 * @param {boolean} props.config.showUserFilter - Show user filter (respects admin role)
 * @param {boolean} props.config.showDateRangeFilter - Show date range filter
 * @param {string} props.config.phoneFieldName - Field name for phone filter (default: "customer.phone_q")
 * @param {string} props.config.cityFieldName - Field name for city filter (default: "customer.city")
 * @param {string} props.config.phonePlaceholder - Placeholder for phone input
 * @param {string} props.config.entityName - Name of the entity being filtered (for translations)
 */
export default function BaseFilter({ 
  onFilter, 
  currentFilters = {}, 
  statusOptions = [],
  config = {}
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const [filters, setFilters] = useState(currentFilters);

  // Default configuration
  const defaultConfig = {
    showPhoneFilter: true,
    showCityFilter: true,
    showUserFilter: true,
    showDateRangeFilter: true,
    phoneFieldName: "customer.phone_q",
    cityFieldName: "customer.city",
    phonePlaceholder: "0600000000",
    entityName: "entity",
    maxTabCols: 6,
    ...config
  };

  // Update local filters when currentFilters prop changes
  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const handleDateRangeChange = ({ from, to }) => {
    const newFilters = {
      ...filters,
    };
    
    if (from && to) {
      newFilters.from = startOfDay(from);
      newFilters.to = endOfDay(to);
    } else {
      // Remove date filters if no dates selected
      delete newFilters.from;
      delete newFilters.to;
    }
    
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const handleStatusChange = (value) => {
    handleFilterChange("status", value === "all" ? "" : value);
  };

  const getCurrentTab = () => {
    if (!filters.status || filters.status === "") return "all";
    return filters.status;
  };

  const clearAllFilters = () => {
    const clearedFilters = { limit: filters.limit || 10 };
    setFilters(clearedFilters);
    onFilter(clearedFilters);
  };

  const hasActiveFilters = () => {
    return Object.keys(filters).some(key => 
      key !== 'limit' && 
      filters[key] !== undefined && 
      filters[key] !== null && 
      filters[key] !== ""
    );
  };

  // Calculate grid columns based on visible filters
  const getVisibleFiltersCount = () => {
    let count = 0;
    if (defaultConfig.showPhoneFilter) count++;
    if (defaultConfig.showCityFilter) count++;
    if (defaultConfig.showUserFilter && auth.isAdmin) count++;
    if (defaultConfig.showDateRangeFilter) count++;
    return count;
  };

  const getGridCols = () => {
    const count = getVisibleFiltersCount();
    if (count === 0) return "grid-cols-1";
    if (count === 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-1 md:grid-cols-2";
    if (count === 3) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
  };

  const getTabGridCols = () => {
    const cols = Math.min(statusOptions.length + 1, defaultConfig.maxTabCols); // +1 for "All" tab
    switch (cols) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-2";
      case 3: return "grid-cols-3";
      case 4: return "grid-cols-4";
      case 5: return "grid-cols-5";
      case 6: return "grid-cols-6";
      default: return "grid-cols-6";
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Tabs */}
      {statusOptions.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Tabs value={getCurrentTab()} onValueChange={handleStatusChange} className="flex-1">
            <TabsList className={`grid w-full ${getTabGridCols()} max-w-4xl`}>
              <TabsTrigger value="all">{t("main.all")}</TabsTrigger>
              {statusOptions.map((status) => (
                <TabsTrigger key={status.value} value={status.value}>
                  {t(status.label)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {hasActiveFilters() && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearAllFilters}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              {t("action.clearFilters")}
            </Button>
          )}
        </div>
      )}

      {/* Additional Filters */}
      {getVisibleFiltersCount() > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className={`grid ${getGridCols()} gap-4`}>
              {/* Phone Search */}
              {defaultConfig.showPhoneFilter && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("customer.phone")}
                  </label>
                  <Input
                    placeholder={defaultConfig.phonePlaceholder}
                    value={filters[defaultConfig.phoneFieldName] || ""}
                    onChange={(e) =>
                      handleFilterChange(defaultConfig.phoneFieldName, e.target.value)
                    }
                  />
                </div>
              )}

              {/* City Filter */}
              {defaultConfig.showCityFilter && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("customer.city")}
                  </label>
                  <SelectCity
                    key={`city-${filters[defaultConfig.cityFieldName] || 'none'}`}
                    city={filters[defaultConfig.cityFieldName]}
                    onSelectCity={(value) =>
                      handleFilterChange(defaultConfig.cityFieldName, value?.name || "")
                    }
                  />
                </div>
              )}

              {/* User Filter - Only for Admins */}
              {defaultConfig.showUserFilter && auth.isAdmin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("user.user")}
                  </label>
                  <SelectUser
                    key={`user-${filters.user || 'none'}`}
                    user={filters.user}
                    onSelectUser={(value) => handleFilterChange("user", value)}
                  />
                </div>
              )}

              {/* Date Range Filter */}
              {defaultConfig.showDateRangeFilter && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("common.dateRange")}
                  </label>
                  <DatePickerWithRange
                    key={`date-picker-${filters.from || ''}-${filters.to || ''}`}
                    onSelectDate={handleDateRangeChange}
                    initialDate={{
                      from: filters.from ? new Date(filters.from) : undefined,
                      to: filters.to ? new Date(filters.to) : undefined,
                    }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
